import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { siteInfo } from '../data/mock';
import { Menu, X, Phone, Mail, Globe } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#home', label: t.nav.home },
    { href: '#tours', label: t.nav.tours },
    { href: '#transfers', label: t.nav.transfers },
    { href: '#areas', label: t.nav.areas },
    { href: '#gallery', label: t.nav.gallery },
    { href: '#contact', label: t.nav.contact },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      {/* Top Bar */}
      <div className={`hidden md:block border-b transition-colors duration-300 ${
        isScrolled ? 'border-stone-200 bg-stone-50' : 'border-white/20 bg-black/20'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-2 flex justify-between items-center text-sm">
          <div className={`flex items-center gap-6 ${isScrolled ? 'text-stone-600' : 'text-white/90'}`}>
            <a href={`mailto:${siteInfo.email}`} className="flex items-center gap-2 hover:text-teal-500 transition-colors">
              <Mail className="w-4 h-4" />
              {siteInfo.email}
            </a>
            <a href={siteInfo.whatsappLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-teal-500 transition-colors">
              <Phone className="w-4 h-4" />
              {siteInfo.whatsapp}
            </a>
          </div>
          <button
            onClick={toggleLanguage}
            className={`flex items-center gap-2 font-medium hover:text-teal-500 transition-colors ${
              isScrolled ? 'text-stone-700' : 'text-white'
            }`}
          >
            <Globe className="w-4 h-4" />
            {language === 'en' ? 'Français' : 'English'}
          </button>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="#home" className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
              isScrolled ? 'bg-teal-600 text-white' : 'bg-white text-teal-600'
            }`}>
              BA
            </div>
            <div>
              <h1 className={`text-xl font-bold tracking-tight ${isScrolled ? 'text-stone-800' : 'text-white'}`}>
                Bali Auto Tour
              </h1>
              <p className={`text-xs ${isScrolled ? 'text-stone-500' : 'text-white/70'}`}>
                Tours & Transfers
              </p>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className={`text-sm font-medium transition-colors hover:text-teal-500 ${
                  isScrolled ? 'text-stone-700' : 'text-white'
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a href={siteInfo.whatsappLink} target="_blank" rel="noopener noreferrer">
              <Button className="bg-teal-600 hover:bg-teal-700 text-white px-6">
                {t.tours.bookNow}
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-4">
            <button
              onClick={toggleLanguage}
              className={`p-2 ${isScrolled ? 'text-stone-700' : 'text-white'}`}
            >
              <Globe className="w-5 h-5" />
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 ${isScrolled ? 'text-stone-700' : 'text-white'}`}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-xl border-t">
            <div className="px-6 py-4 space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="block text-stone-700 font-medium hover:text-teal-600 transition-colors py-2"
                >
                  {link.label}
                </a>
              ))}
              <a
                href={siteInfo.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white">
                  {t.tours.bookNow}
                </Button>
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;